import { QRCodeSVG } from 'qrcode.react';
import type { TemplateData } from './template-types';
import { TIPO_LABEL, SECCION_LABEL } from './template-types';

const ACCENT = '#2563eb';

function QRBox({ url }: { url?: string }) {
  if (url) {
    return (
      <div style={{ width: 72, height: 72, border: '1.5px solid #ccc', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <QRCodeSVG value={url} size={64} />
      </div>
    );
  }
  return (
    <div style={{ width: 72, height: 72, border: '1.5px solid #ccc', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <svg viewBox="0 0 21 21" width="64" height="64">
        {[0,1,2,3,4,5,6].map(r => [0,1,2,3,4,5,6].map(c => {
          const p = [[1,1,1,1,1,1,1],[1,0,0,0,0,0,1],[1,0,1,1,1,0,1],[1,0,1,1,1,0,1],[1,0,1,1,1,0,1],[1,0,0,0,0,0,1],[1,1,1,1,1,1,1]];
          return p[r]?.[c] ? <rect key={`a-${r}-${c}`} x={c*3} y={r*3} width={3} height={3} fill="#333" /> : null;
        }))}
        {[0,1,2,3,4,5,6].map(r => [0,1,2,3,4,5,6].map(c => {
          const p = [[1,1,1,1,1,1,1],[1,0,0,0,0,0,1],[1,0,1,1,1,0,1],[1,0,1,1,1,0,1],[1,0,1,1,1,0,1],[1,0,0,0,0,0,1],[1,1,1,1,1,1,1]];
          return p[r]?.[c] ? <rect key={`b-${r}-${c}`} x={(14+c)*3} y={r*3} width={3} height={3} fill="#333" /> : null;
        }))}
        {[0,1,2,3,4,5,6].map(r => [0,1,2,3,4,5,6].map(c => {
          const p = [[1,1,1,1,1,1,1],[1,0,0,0,0,0,1],[1,0,1,1,1,0,1],[1,0,1,1,1,0,1],[1,0,1,1,1,0,1],[1,0,0,0,0,0,1],[1,1,1,1,1,1,1]];
          return p[r]?.[c] ? <rect key={`c-${r}-${c}`} x={c*3} y={(14+r)*3} width={3} height={3} fill="#333" /> : null;
        }))}
      </svg>
    </div>
  );
}

function Contenido({ data }: { data: TemplateData }) {
  if (data.tipo === 'receta') return (
    <>
      {data.medicaciones?.map((m, i) => (
        <div key={i} style={{ padding: '10px 0', borderBottom: '1px solid #f0f0f0' }}>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
            <span style={{ color: ACCENT, fontWeight: 700, fontSize: 13 }}>•</span>
            <span style={{ fontWeight: 600, fontSize: 12 }}>{m.denominacionGenerica}{m.denominacionDistintiva ? <span style={{ fontWeight: 400, color: '#777', fontSize: 11 }}> ({m.denominacionDistintiva})</span> : ''}</span>
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16, marginTop: 3, marginLeft: 16, fontSize: 10.5, color: '#555' }}>
            {m.dosis             && <span>Dosis: {m.dosis}</span>}
            {m.frecuencia        && <span>Frecuencia: {m.frecuencia}</span>}
            {m.duracion          && <span>Duración: {m.duracion}</span>}
            {m.viaAdministracion && <span>Vía: {m.viaAdministracion}</span>}
          </div>
          {m.indicaciones && <div style={{ marginLeft: 16, marginTop: 2, fontSize: 10, color: '#888', fontStyle: 'italic' }}>{m.indicaciones}</div>}
        </div>
      ))}
      {(!data.medicaciones?.length) && <p style={{ color: '#aaa', fontStyle: 'italic' }}>No se incluyeron medicamentos.</p>}
    </>
  );
  if (data.tipo === 'orden') return (
    <div style={{ fontSize: 12, lineHeight: 1.8, color: '#333', whiteSpace: 'pre-wrap' }}>
      {data.instrucciones || <span style={{ color: '#aaa', fontStyle: 'italic' }}>Sin indicaciones.</span>}
    </div>
  );
  if (data.tipo === 'presupuesto') {
    const items    = data.presupuestoItems ?? [];
    const moneda   = data.presupuestoMoneda ?? 'MXN';
    const subtotal = items.reduce((s, i) => s + i.cantidad * i.precioUnitario, 0);
    const descPct  = data.presupuestoDescuento ?? 0;
    const descAmt  = subtotal * (descPct / 100);
    const ivaPct   = data.presupuestoIva ?? 0;
    const ivaAmt   = (subtotal - descAmt) * (ivaPct / 100);
    const total    = subtotal - descAmt + ivaAmt;
    const fmt      = (n: number) => `$${n.toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    return (
      <div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr auto auto auto', gap: '4px 12px', padding: '5px 0', borderBottom: '1px solid #e5e7eb', fontSize: 10, fontWeight: 700, color: '#888', textTransform: 'uppercase', letterSpacing: 0.5 }}>
          <span>Servicio</span>
          <span style={{ textAlign: 'right', minWidth: 28 }}>Cant.</span>
          <span style={{ textAlign: 'right', minWidth: 72 }}>Precio u.</span>
          <span style={{ textAlign: 'right', minWidth: 80 }}>Total</span>
        </div>
        {items.map((item, i) => (
          <div key={i} style={{ display: 'grid', gridTemplateColumns: '1fr auto auto auto', gap: '4px 12px', padding: '8px 0', borderBottom: '1px solid #f0f0f0', fontSize: 11, alignItems: 'center' }}>
            <div>
              <div style={{ fontWeight: 600 }}>{item.servicio}</div>
              {item.descripcion && <div style={{ fontSize: 9.5, color: '#aaa' }}>{item.descripcion}</div>}
            </div>
            <span style={{ textAlign: 'right', color: '#888', minWidth: 28 }}>{item.cantidad}</span>
            <span style={{ textAlign: 'right', minWidth: 72 }}>{fmt(item.precioUnitario)}</span>
            <span style={{ textAlign: 'right', fontWeight: 600, minWidth: 80 }}>{fmt(item.cantidad * item.precioUnitario)}</span>
          </div>
        ))}
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 3, marginTop: 10, fontSize: 11 }}>
          <div style={{ display: 'flex', gap: 16 }}><span style={{ color: '#aaa' }}>Subtotal</span><span style={{ minWidth: 80, textAlign: 'right' }}>{fmt(subtotal)}</span></div>
          {descPct > 0 && <div style={{ display: 'flex', gap: 16 }}><span style={{ color: '#aaa' }}>Descuento ({descPct}%)</span><span style={{ minWidth: 80, textAlign: 'right', color: '#16a34a' }}>−{fmt(descAmt)}</span></div>}
          {ivaPct > 0  && <div style={{ display: 'flex', gap: 16 }}><span style={{ color: '#aaa' }}>IVA ({ivaPct}%)</span><span style={{ minWidth: 80, textAlign: 'right' }}>{fmt(ivaAmt)}</span></div>}
          <div style={{ display: 'flex', gap: 16, borderTop: '1px solid #111', paddingTop: 5, marginTop: 2 }}>
            <span style={{ fontWeight: 700 }}>TOTAL {moneda}</span>
            <span style={{ minWidth: 80, textAlign: 'right', fontWeight: 700, fontSize: 13 }}>{fmt(total)}</span>
          </div>
          {data.presupuestoValidezDias && <div style={{ fontSize: 9.5, color: '#aaa', marginTop: 4 }}>Vigencia: {data.presupuestoValidezDias} días</div>}
        </div>
      </div>
    );
  }
  if (data.tipo === 'plan-tratamiento') {
    const items = data.planTratamientoItems ?? [];
    return (
      <div>
        {(data.diagnostico || data.objetivoTratamiento) && (
          <div style={{ borderLeft: `2px solid ${ACCENT}`, paddingLeft: 12, marginBottom: 12, fontSize: 11 }}>
            {data.diagnostico        && <div><span style={{ color: '#aaa', fontSize: 10 }}>Diagnóstico</span><br /><b>{data.diagnostico}</b></div>}
            {data.objetivoTratamiento && <div style={{ marginTop: 6 }}><span style={{ color: '#aaa', fontSize: 10 }}>Objetivo</span><br /><b>{data.objetivoTratamiento}</b></div>}
          </div>
        )}
        {items.map((item, i) => (
          <div key={i} style={{ display: 'flex', gap: 8, padding: '8px 0', borderBottom: '1px solid #f0f0f0', alignItems: 'flex-start' }}>
            <span style={{ color: ACCENT, fontWeight: 700, fontSize: 13, flexShrink: 0 }}>{i + 1}.</span>
            <div>
              <div style={{ fontWeight: 600 }}>{item.tratamiento}</div>
              {item.descripcion && <div style={{ fontSize: 10, color: '#888', marginTop: 2 }}>{item.descripcion}</div>}
              <div style={{ display: 'flex', gap: 16, marginTop: 3, fontSize: 10, color: '#aaa' }}>
                {item.sesiones !== undefined && <span>Sesiones: <b style={{ color: '#555' }}>{item.sesiones}</b></span>}
                {item.duracion               && <span>Duración: <b style={{ color: '#555' }}>{item.duracion}</b></span>}
              </div>
            </div>
          </div>
        ))}
        {(data.duracionTratamiento || data.frecuenciaTratamiento) && (
          <div style={{ marginTop: 10, padding: '7px 12px', background: `${ACCENT}10`, borderRadius: 6, fontSize: 10.5, color: '#1e40af' }}>
            {data.duracionTratamiento  && <span><b>Duración total:</b> {data.duracionTratamiento}</span>}
            {data.duracionTratamiento && data.frecuenciaTratamiento && <span style={{ margin: '0 10px', opacity: 0.3 }}>│</span>}
            {data.frecuenciaTratamiento && <span><b>Frecuencia:</b> {data.frecuenciaTratamiento}</span>}
          </div>
        )}
      </div>
    );
  }
  if (data.tipo === 'consentimiento') return (
    <div>
      {data.consentimientoTitulo && <div style={{ fontWeight: 700, fontSize: 12.5, color: '#111', marginBottom: 10 }}>{data.consentimientoTitulo}</div>}
      <div style={{ fontSize: 11.5, lineHeight: 1.8, color: '#333', whiteSpace: 'pre-wrap' }}>
        {data.consentimientoContenido || <span style={{ color: '#aaa', fontStyle: 'italic' }}>Sin contenido.</span>}
      </div>
    </div>
  );
  return (
    <>
      {data.estudios?.map((e, i) => (
        <div key={i} style={{ display: 'flex', gap: 8, padding: '8px 0', borderBottom: '1px solid #f0f0f0', alignItems: 'flex-start' }}>
          <span style={{ color: ACCENT, fontWeight: 700, fontSize: 13 }}>•</span>
          <div>
            <div style={{ fontWeight: 600 }}>{e.nombre}</div>
            {e.descripcion && <div style={{ fontSize: 10.5, color: '#888' }}>{e.descripcion}</div>}
          </div>
        </div>
      ))}
    </>
  );
}

function FirmaPacienteBox({ data }: { data: TemplateData }) {
  if (data.tipo !== 'consentimiento') return null;
  return (
    <div style={{ textAlign: 'center' }}>
      {data.firmaUrl
        ? (data.firmaEsPdf
          ? <div style={{ fontSize: 9.5, color: '#888', fontStyle: 'italic', width: 200 }}>Documento firmado adjunto (PDF)</div>
          : <img src={data.firmaUrl} alt="Firma del paciente" style={{ height: 50, maxWidth: 200, objectFit: 'contain' }} />)
        : <div style={{ height: 50, width: 200 }} />}
      <div style={{ borderTop: '1px solid #555', width: 200, marginTop: 6, marginBottom: 6 }} />
      <div style={{ fontSize: 10.5, fontWeight: 600 }}>{data.paciente.nombre}</div>
      <div style={{ fontSize: 9.5, color: '#888' }}>Firma del paciente</div>
    </div>
  );
}

export function TemplateMinimalista({ data, width = 794, height = 1123 }: { data: TemplateData; width?: number; height?: number }) {
  const tituloDoc = TIPO_LABEL[data.tipo];
  return (
    <div style={{ width, height, fontFamily: "'Helvetica Neue', Arial, sans-serif", fontSize: 11, color: '#1a1a1a', backgroundColor: '#fff', display: 'flex', flexDirection: 'column', boxSizing: 'border-box', padding: '40px 52px 32px' }}>

      {/* Header: Logo + Clinic left | Meta right */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 24 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
          <div style={{ width: 52, height: 52, background: `${ACCENT}18`, borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            {data.clinica.logoUrl
              ? <img src={data.clinica.logoUrl} alt="" style={{ width: 46, height: 46, objectFit: 'contain', borderRadius: 8 }} />
              : <span style={{ fontSize: 22, fontWeight: 800, color: ACCENT }}>{data.clinica.nombre.charAt(0)}</span>}
          </div>
          <div>
            <div style={{ fontWeight: 700, fontSize: 14, color: '#111' }}>{data.clinica.nombre}</div>
            {data.clinica.cedula && <div style={{ fontSize: 10, color: '#888', marginTop: 2 }}>{data.clinica.cedula}</div>}
            <div style={{ fontSize: 10, color: '#999', marginTop: 1 }}>{data.medico.nombre}</div>
          </div>
        </div>

        <div style={{ textAlign: 'right', borderLeft: '2px solid #e5e7eb', paddingLeft: 20 }}>
          <div style={{ fontWeight: 700, fontSize: 12.5, color: ACCENT }}>{tituloDoc}</div>
          <div style={{ fontSize: 10, color: '#aaa', marginTop: 4 }}>Folio</div>
          <div style={{ fontWeight: 700, fontSize: 14, color: '#111' }}>#{data.folio}</div>
          <div style={{ fontSize: 10, color: '#888', marginTop: 2 }}>{data.fecha}</div>
        </div>
      </div>

      {/* Thin separator */}
      <div style={{ borderTop: '1px solid #e5e7eb', marginBottom: 16 }} />

      {/* Patient info grid */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', columnGap: 40, rowGap: 6, fontSize: 11 }}>
        <div><span style={{ color: '#888', fontSize: 10 }}>Paciente</span><br /><b>{data.paciente.nombre}</b></div>
        <div><span style={{ color: '#888', fontSize: 10 }}>Fecha de nacimiento</span><br /><b>{data.paciente.fechaNacimiento ?? '—'}</b></div>
        <div><span style={{ color: '#888', fontSize: 10 }}>Médico</span><br /><b>{data.medico.nombre}</b></div>
        <div><span style={{ color: '#888', fontSize: 10 }}>Fecha de consulta</span><br /><b>{data.fechaConsulta ?? '—'}</b></div>
      </div>

      {/* Thin separator */}
      <div style={{ borderTop: '1px solid #e5e7eb', margin: '18px 0' }} />

      {/* Section title */}
      <div style={{ fontSize: 12.5, fontWeight: 700, color: ACCENT, marginBottom: 12, textTransform: 'uppercase', letterSpacing: 0.8 }}>
        {SECCION_LABEL[data.tipo]}
      </div>

      {/* Content */}
      <div style={{ flex: 1, overflow: 'hidden' }}>
        <Contenido data={data} />
      </div>

      {/* QR + signature(s) */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginTop: 48 }}>
        <QRBox url={data.qrUrl} />
        <FirmaPacienteBox data={data} />
        <div style={{ textAlign: 'center' }}>
          <div style={{ borderTop: '1px solid #555', width: 200, marginBottom: 6 }} />
          <div style={{ fontSize: 10.5, fontWeight: 600 }}>{data.medico.nombre}</div>
          {data.medico.cedula && <div style={{ fontSize: 9.5, color: '#888' }}>Cédula: {data.medico.cedula}</div>}
        </div>
      </div>

      {/* Thin separator */}
      <div style={{ borderTop: '1px solid #e5e7eb', margin: '20px 0 8px' }} />

      {/* Footer */}
      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 9, color: '#aaa' }}>
        <span>{data.clinica.direccion}</span>
        {data.clinica.telefono && <span>Tel. {data.clinica.telefono}</span>}
      </div>
    </div>
  );
}
